import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { UsersQuery } from "lib/generated/app-service-gql/graphql";
import { useAssertCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { useUsersGraphql } from "lib/hooks/api/users/useUsersGraphql";

type UseUsersArgs = {
  /**
   * Include deactivated users in the result. Default: false.
   * Use `true` on display surfaces that need to resolve already-assigned
   * users who have since been deactivated (e.g. comment @mention pills,
   * saved-view owner cells, buyer-list permissions modal).
   */
  includeInactive?: boolean;
};

/**
 * The signed-in user's company members, for pickers and name lookups. Stotles staff are only
 * listed in demo companies. app-service returns active users first, so with `includeInactive` a
 * lookup by name finds an active user before a deactivated namesake.
 */
export function useUsers(
  args: UseUsersArgs = {},
  options?: UseQueryOptions<UsersQuery, unknown, UsersQuery, QueryKey>,
) {
  const currentUser = useAssertCurrentUser();
  const includeInactive = !!args.includeInactive;

  return useUsersGraphql(
    {
      ...(includeInactive ? {} : { active: true }),
      ...(currentUser.company.demo ? {} : { isAdmin: false }),
    },
    options,
  );
}
